import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OwnerPatchBrandingRequest } from '@naya/shared'
import { ownerApi } from '../ownerApi.js'
import { ownerMeKey } from './useOwnerSession.js'
import type {
  DeleteLogoResponse,
  PatchBrandingResponse,
  UploadLogoResponse,
} from '../types.js'

/**
 * Branding mutations — business name + logo. All three invalidate the owner
 * session query (`ownerMeKey`) since `me.tenant` carries the business name and
 * logo_url that the shell header + dashboard render.
 */

export function usePatchBranding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: OwnerPatchBrandingRequest) =>
      ownerApi.patch<PatchBrandingResponse>('/owner/branding', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerMeKey })
    },
  })
}

export function useUploadLogo() {
  const qc = useQueryClient()
  return useMutation({
    // Accepts a Blob — the branding page sends a cropped canvas Blob, which is
    // the same multipart wire shape as a File to Fastify's multipart parser.
    mutationFn: (blob: Blob) => {
      const form = new FormData()
      // Field name must be `logo` — the API reads the first file part. The
      // explicit filename keeps the part well-formed for a raw Blob.
      form.append('logo', blob, 'logo.png')
      return ownerApi.upload<UploadLogoResponse>('/owner/branding/logo', form)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerMeKey })
    },
  })
}

export function useDeleteLogo() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => ownerApi.del<DeleteLogoResponse>('/owner/branding/logo'),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ownerMeKey })
    },
  })
}
